import type { AxiosResponse } from 'axios';
import type { InputDataOrVoid } from '../NoddiAsync';
import { getAccountUrls } from './accounts';
import { getAdapterUrls } from './adapters';
import { getAddressUrls } from './addresses';
import { getBookingItemUrls } from './bookingItems';
import { getBookingNoteUrls } from './bookingNotes';
import { getBookingUrls } from './bookings';
import { getBookingProposalNoteUrls } from './bookings/proposalNoteUrls';
import { getBookingUpdateOfferUrls } from './bookingUpdateOffers';
import { getBookingUpdateRequestUrls } from './bookingUpdateRequests';
import { getBrandUrls } from './brands';
import { getCampaignUrls } from './campaigns';
import { getLeadTireOfferUrls } from './campaigns/leadTireOffers';
import { getPartnerReferralUrls } from './campaigns/partnerReferrals';
import { getCapacityUrls } from './capacity';
import { getCarUrls } from './cars';
import { getChatAgentUrls } from './chatAgent';
import { getCommunicationUrls } from './communications';
import { getCapabilityUrls } from './competences';
import { getCoreUrls } from './core';
import { getCountrySettingUrls } from './countrySettings';
import { getCreditMemoUrls } from './creditMemos';
import { getCustomerContractNoteUrls } from './customerContractNotes';
import { getDashboardUrls } from './dashboard';
import { type InferServerRequests, keyMirror } from './defineResource';
import { getDeliveryWindowUrls } from './deliveryWindows';
import { getServiceDepartmentUrls } from './departments';
import { getDeviceUrls } from './devices';
import { getEquipmentUrls } from './equipment';
import { getFleetScheduleUrls } from './fleetSchedules';
import { getForecastUrls } from './forecast';
import { getGarageUrls } from './garages';
import { getIamUrls } from './iam';
import { getImpersonationBypassUrls } from './impersonationBypass';
import { getInspectionUrls } from './inspections';
import { getTireInventoryUrls } from './inventory';
import { getInvoiceUrls } from './invoices';
import { getMembershipUrls } from './memberships';
import { getNotificationUrls } from './notifications';
import { getOpsUrls } from './ops';
import { getOrderUrls } from './orders';
import { getOrganizationUrls } from './organizations';
import { getParkingPlaceEntranceUrls, getParkingPlaceUrls, getParkingZoneUrls } from './parkingPlaces';
import { getPayrollUrls } from './payroll';
import { getRecallProgramTemplateUrls, getRecallReminderUrls, getRecallUrls } from './recalls';
import { getReloadStopUrls } from './reloadStops';
import { getRevenueUrls } from './revenue';
import { getRouteUrls } from './routes';
import { getRuleSetUrls } from './rules';
import { getSalesItemsUrls } from './salesItems';
import { getFeedbackUrls } from './serviceFeedbacks';
import { getServiceOrganizationCouponUrls } from './serviceOrganizationCoupons';
import { getServiceVehicleUrls } from './serviceVehicles';
import { getWorkerUrls } from './serviceWorkers';
import { getTireOfferNudgeUrls, getTireQuoteOptionUrls, getTireQuoteUrls } from './shop';
import { getSignageDashboardUrls } from './signageDashboards';
import { getSignagePlaylistUrls } from './signagePlaylists';
import { getSiteGroupUrls } from './siteGroups';
import { getSitePortUrls } from './sitePorts';
import { getSiteUrls } from './sites';
import { getSlackUrls } from './slack';
import { getSmsUrls } from './sms';
import { getStationUrls } from './stations';
import { getStorageUrls } from './storage';
import { getStorageFacilityStopTimingUrls } from './storageFacilityStopTimings';
import { getSupportAiSettingsUrls } from './supportAiSettings';
import { getSupportConversationUrls } from './supportConversations';
import { getSupportInboxUrls } from './supportInboxes';
import { getSupportScheduleUrls } from './supportSchedules';
import { getTaskUrls } from './tasks';
import { getTicketEventMentionUrls } from './ticketEventMentions';
import { getTicketUrls } from './tickets';
import { getTimeTrackingUrls } from './timeTracking';
import { getTireHotelLocationUrls, getTireHotelUrls } from './tireHotels';
import { getUnableToCompleteUrls } from './unableToComplete';
import type { ResponseType } from './url';
import { getUserGroupDataQualityUrls } from './userGroupDataQuality';
import { getUserGroupUrls } from './userGroups';
import { getUserMergeUrls } from './userMerges';
import { getWheelChangeTimingUrls } from './wheelChangeTiming';
import { getWmsUrls } from './wms';
import { getWorkstationSlotUrls } from './workstationSlots';

/**
 * Single source of truth for the endpoint registry.
 *
 * Each resource module declares its endpoints once via `dataEndpoint()` / `responseEndpoint()`
 * (see {@link import('./defineResource')}) and exports only its `get<Resource>Urls`
 * map. This aggregator merges those maps, then derives everything else —
 * {@link URLKeys}, {@link URLKeysTypes}, and the internal `ServerRequests` type —
 * from the merged map. Adding a resource is one import + one spread line.
 *
 * `rawUrls` keeps the precise per-endpoint types (including the type-only
 * `__server` phantom each entry carries) so `ServerRequests` can be reconstructed;
 * {@link getUrls} is the same object widened to the runtime-config shape the
 * `noddiAsync` hook pipeline consumes.
 */
const rawUrls = {
  ...getAdapterUrls,
  ...getAddressUrls,
  ...getBookingItemUrls,
  ...getBookingNoteUrls,
  ...getBookingProposalNoteUrls,
  ...getBookingUrls,
  ...getBookingUpdateOfferUrls,
  ...getBookingUpdateRequestUrls,
  ...getBrandUrls,
  ...getCampaignUrls,
  ...getPartnerReferralUrls,
  ...getLeadTireOfferUrls,
  ...getServiceOrganizationCouponUrls,
  ...getCapabilityUrls,
  ...getCapacityUrls,
  ...getCarUrls,
  ...getCountrySettingUrls,
  ...getCoreUrls,
  ...getCreditMemoUrls,
  ...getCustomerContractNoteUrls,
  ...getDashboardUrls,
  ...getDeviceUrls,
  ...getSignageDashboardUrls,
  ...getSignagePlaylistUrls,
  ...getSiteGroupUrls,
  ...getParkingPlaceEntranceUrls,
  ...getParkingPlaceUrls,
  ...getParkingZoneUrls,
  ...getSitePortUrls,
  ...getSiteUrls,
  ...getIamUrls,
  ...getInspectionUrls,
  ...getDeliveryWindowUrls,
  ...getEquipmentUrls,
  ...getFleetScheduleUrls,
  ...getFeedbackUrls,
  ...getGarageUrls,
  ...getInvoiceUrls,
  ...getMembershipUrls,
  ...getNotificationUrls,
  ...getOrderUrls,
  ...getOrganizationUrls,
  ...getPayrollUrls,
  ...getRecallUrls,
  ...getRecallProgramTemplateUrls,
  ...getRecallReminderUrls,
  ...getReloadStopUrls,
  ...getRouteUrls,
  ...getStorageFacilityStopTimingUrls,
  ...getRuleSetUrls,
  ...getSalesItemsUrls,
  ...getSlackUrls,
  ...getStationUrls,
  ...getServiceDepartmentUrls,
  ...getServiceVehicleUrls,
  ...getSmsUrls,
  ...getStorageUrls,
  ...getSupportAiSettingsUrls,
  ...getSupportConversationUrls,
  ...getSupportInboxUrls,
  ...getSupportScheduleUrls,
  ...getTaskUrls,
  ...getOpsUrls,
  ...getTicketUrls,
  ...getTimeTrackingUrls,
  ...getForecastUrls,
  ...getRevenueUrls,
  ...getTicketEventMentionUrls,
  ...getChatAgentUrls,
  ...getCommunicationUrls,
  ...getTireHotelLocationUrls,
  ...getTireHotelUrls,
  ...getTireInventoryUrls,
  ...getTireQuoteUrls,
  ...getTireOfferNudgeUrls,
  ...getTireQuoteOptionUrls,
  ...getUnableToCompleteUrls,
  ...getAccountUrls,
  ...getUserGroupDataQualityUrls,
  ...getUserMergeUrls,
  ...getUserGroupUrls,
  ...getWheelChangeTimingUrls,
  ...getWmsUrls,
  ...getWorkerUrls,
  ...getWorkstationSlotUrls,
  ...getImpersonationBypassUrls
};

type ServerRequests = InferServerRequests<typeof rawUrls>;

export const URLKeys = keyMirror(rawUrls);

export type URLKeysTypes = keyof typeof rawUrls;

export type InputData<urlKey extends URLKeysTypes> = ServerRequests[urlKey]['input'];
export type OutputData<urlKey extends URLKeysTypes> = ServerRequests[urlKey]['output'];

/**
 * Sort keys an endpoint accepts, read off the `order` its input declares (which
 * `ApiInput` derives from the nsdk `ordering` union -- see `OrderingKeyOf`).
 *
 * Keyed by URL key, so a table names the endpoint it actually calls:
 *
 * ```ts
 * type CarOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedCars>;
 * ```
 *
 * `never` for endpoints that take no ordering, so a sortable column on one
 * cannot be typed at all.
 */
export type OrderingKeyOfUrlKey<Key extends URLKeysTypes> = 'order' extends keyof InputData<Key>
  ? InputData<Key> extends { order?: Array<{ column: infer Column }> }
    ? Column & string
    : never
  : // `order` is optional, so an input without one still matches the shape
    // above and infers `unknown`, i.e. `string`. The `keyof` check is what
    // makes this fail closed.
    never;

export type NoddiAsyncReturnType<Type extends URLKeysTypes, _> = OutputData<Type>;

type CommonAxiosResType<key extends URLKeysTypes> = AxiosResponse<OutputData<key>, unknown>;

export const getUrls: {
  [key in URLKeysTypes]: {
    getUrl: (input: InputDataOrVoid<key>) => string;
    getBody?: (input: InputDataOrVoid<key>) => Partial<InputData<key>> | FormData;
    responseType?: ResponseType;
    handleRes?: (res: CommonAxiosResType<key>) => Promise<unknown>;
    getHeaders?: (input: InputDataOrVoid<key>) => Record<string, string>;
    ignoreImpersonation?: boolean;
    skipConvertingToCamelCase?: boolean;
    skipBodyTransformation?: boolean;
    /** Side-effect GET: useGet merges gcTime: 0 by default; caller queryConfig overrides. */
    sideEffectGet?: true;
  };
} = rawUrls;
